import { appStorage } from './betaStorage'
import type { YouthParticipantId } from './youthParticipants'

export type YouthTileKind = 'routine' | 'school' | 'tasks'

export interface YouthSupportStrategy {
  id: string
  label: string
  icon: string
}

export interface YouthCommunicationChoice {
  id: string
  label: string
  icon: string
}

export interface YouthSupportProfile {
  whatHelps: string[]
  whatMakesThingsWorse: string[]
  sensoryPreferences: string[]
  communicationPreferences: string[]
  signsINeedHelp: string[]
  preferredSupportPeople: string[]
  calmingStrategies: string[]
}

export interface YouthRoutineStep {
  id: string
  text: string
  icon: string
}

export interface YouthDayPrompt {
  weekday: number
  label: string
  activity: string
  pack: YouthRoutineStep[]
}

export interface YouthTileConfig {
  id: 'morning' | 'school' | 'home' | 'bedtime'
  title: string
  subtitle: string
  icon: string
  kind: YouthTileKind
  steps: YouthRoutineStep[]
  schoolDays?: YouthDayPrompt[]
}

export interface YouthRewardGoal {
  title: string
  icon: string
  targetPoints: number
}

export interface YouthSupportPlan {
  regulationStrategies: YouthSupportStrategy[]
  tellSomeoneChoices: YouthCommunicationChoice[]
  supportProfile: YouthSupportProfile
  tiles: YouthTileConfig[]
  rewardGoal: YouthRewardGoal
}

const LEGACY_STORAGE_KEY = 'schub.youthSupportPlan.v1'
const STORAGE_PREFIX = 'marnixia.youthSupportPlan.v2.'
export const YOUTH_SUPPORT_PLAN_UPDATED = 'schub:youth-support-plan'

export const defaultYouthSupportPlan: YouthSupportPlan = {
  regulationStrategies: [
    { id: 'quiet', label: 'Quiet space', icon: '🤫' },
    { id: 'headphones', label: 'Headphones', icon: '🎧' },
    { id: 'drink', label: 'Drink', icon: '🥤' },
    { id: 'breathe', label: 'Breathe', icon: '🌬️' },
    { id: 'talk', label: 'Talk to Mum', icon: '💬' },
    { id: 'break', label: 'I need a break', icon: '🧘' },
  ],
  tellSomeoneChoices: [
    { id: 'help', label: 'I need help', icon: '🙋' },
    { id: 'talk', label: 'I want to talk', icon: '💬' },
    { id: 'break', label: 'I need a break', icon: '🧘' },
    { id: 'dont-understand', label: "I don't understand", icon: '❓' },
    { id: 'too-loud', label: 'It is too loud', icon: '🔊' },
    { id: 'need-space', label: 'I need some space', icon: '↔️' },
    { id: 'please-stop', label: 'Please stop', icon: '✋' },
  ],
  supportProfile: {
    whatHelps: ['Give me time to process', 'Use calm, clear language', 'Show me what is happening next'],
    whatMakesThingsWorse: ['Too many questions at once', 'Rushing me', 'Loud or busy spaces'],
    sensoryPreferences: ['Headphones can help with noise', 'Offer a quieter space when possible'],
    communicationPreferences: ['Use short clear sentences', 'Give me time to answer', 'Visual choices can help'],
    signsINeedHelp: ['I may go quiet', 'I may repeat myself', 'I may try to leave the area'],
    preferredSupportPeople: ['Mum / Plan Nominee'],
    calmingStrategies: ['Quiet space', 'Headphones', 'Drink of water', 'Take a break'],
  },
  rewardGoal: { title: 'Choose a movie night', icon: '🎬', targetPoints: 20 },
  tiles: [
    {
      id: 'morning', title: 'Morning', subtitle: 'My morning routine', icon: '☀️', kind: 'routine',
      steps: [
        { id: 'morning-bed', text: 'Make my bed', icon: '🛏️' },
        { id: 'morning-dress', text: 'Get dressed', icon: '👕' },
        { id: 'morning-teeth', text: 'Brush my teeth', icon: '🪥' },
        { id: 'morning-breakfast', text: 'Eat breakfast', icon: '🥣' },
        { id: 'morning-bag', text: 'Get my school bag ready', icon: '🎒' },
      ],
    },
    {
      id: 'school', title: 'School', subtitle: 'What do I need to pack today?', icon: '🎒', kind: 'school', steps: [],
      schoolDays: [
        { weekday: 1, label: 'Monday', activity: 'Music', pack: [{ id: 'mon-water', text: 'Water bottle', icon: '💧' }, { id: 'mon-lunch', text: 'Lunch box', icon: '🍎' }, { id: 'mon-music', text: 'Music folder', icon: '🎵' }] },
        { weekday: 2, label: 'Tuesday', activity: 'Sport', pack: [{ id: 'tue-water', text: 'Water bottle', icon: '💧' }, { id: 'tue-lunch', text: 'Lunch box', icon: '🍎' }, { id: 'tue-sport', text: 'Sports shoes', icon: '👟' }] },
        { weekday: 3, label: 'Wednesday', activity: 'Cooking', pack: [{ id: 'wed-water', text: 'Water bottle', icon: '💧' }, { id: 'wed-lunch', text: 'Lunch box', icon: '🍎' }, { id: 'wed-cook', text: 'Cooking apron', icon: '🧑‍🍳' }] },
        { weekday: 4, label: 'Thursday', activity: 'Library', pack: [{ id: 'thu-water', text: 'Water bottle', icon: '💧' }, { id: 'thu-lunch', text: 'Lunch box', icon: '🍎' }, { id: 'thu-library', text: 'Library bag', icon: '📚' }] },
        { weekday: 5, label: 'Friday', activity: 'Swimming', pack: [{ id: 'fri-water', text: 'Water bottle', icon: '💧' }, { id: 'fri-lunch', text: 'Lunch box', icon: '🍎' }, { id: 'fri-swim', text: 'Swimmers + towel', icon: '🏊' }] },
      ],
    },
    {
      id: 'home', title: 'After School', subtitle: 'My after school jobs', icon: '🏠', kind: 'tasks',
      steps: [
        { id: 'home-bag', text: 'Put my school bag away', icon: '🎒' },
        { id: 'home-shoes', text: 'Put my shoes away', icon: '👟' },
        { id: 'home-snack', text: 'Have a snack and drink', icon: '🍎' },
        { id: 'home-break', text: 'Have some quiet time', icon: '🎧' },
        { id: 'home-job', text: 'Check if I have a home job', icon: '✅' },
      ],
    },
    {
      id: 'bedtime', title: 'Bedtime', subtitle: 'My bedtime routine', icon: '🌙', kind: 'routine',
      steps: [
        { id: 'bed-pjs', text: 'Put PJs on', icon: '👕' },
        { id: 'bed-teeth', text: 'Brush teeth', icon: '🪥' },
        { id: 'bed-book', text: 'Read a book', icon: '📖' },
        { id: 'bed-goodnight', text: 'Say goodnight to my family', icon: '💜' },
        { id: 'bed-sleep', text: 'Sleep', icon: '😴' },
      ],
    },
  ],
}

export const defaultLizzieSupportPlan: YouthSupportPlan = {
  ...JSON.parse(JSON.stringify(defaultYouthSupportPlan)) as YouthSupportPlan,
  regulationStrategies: [
    { id: 'mum', label: 'Mum please', icon: '' },
    { id: 'quiet', label: 'Quiet corner', icon: '' },
    { id: 'headphones', label: 'Headphones', icon: '' },
    { id: 'cuddle', label: 'Cuddle', icon: '' },
    { id: 'drink', label: 'Drink', icon: '' },
    { id: 'break', label: 'I need a break', icon: '' },
  ],
  tellSomeoneChoices: [
    { id: 'help', label: 'Help please', icon: '' },
    { id: 'mum', label: 'I want Mum', icon: '' },
    { id: 'break', label: 'I need a break', icon: '' },
    { id: 'more', label: 'More please', icon: '' },
    { id: 'stop', label: 'Stop please', icon: '' },
    { id: 'too-loud', label: 'Too loud', icon: '' },
  ],
  supportProfile: {
    whatHelps: ['Use first–then pictures', 'Offer two simple choices', 'Give Lizzie time to respond'],
    whatMakesThingsWorse: ['Long explanations', 'Sudden changes', 'Busy or noisy rooms'],
    sensoryPreferences: ['Headphones for busy noise', 'A quiet corner with a soft toy'],
    communicationPreferences: ['Use short phrases', 'Show the matching picture', 'Accept pointing or tapping as communication'],
    signsINeedHelp: ['Lizzie may cover her ears', 'Lizzie may cry or lie down', 'Lizzie may call for Mum'],
    preferredSupportPeople: ['Mum / Lillian'],
    calmingStrategies: ['Quiet corner', 'Headphones', 'Cuddle if Lizzie asks', 'NIXI break choice'],
  },
  rewardGoal: { title: 'Choose our bedtime story', icon: '', targetPoints: 10 },
  tiles: [
    { id:'morning',title:'Morning',subtitle:'Get ready with NIXI',icon:'',kind:'routine',steps:[{id:'liz-morning-toilet',text:'Go to the toilet',icon:''},{id:'liz-morning-dress',text:'Get dressed',icon:''},{id:'liz-morning-breakfast',text:'Eat breakfast',icon:''},{id:'liz-morning-teeth',text:'Brush my teeth',icon:''},{id:'liz-morning-bag',text:'Pack my preschool bag',icon:''},{id:'liz-morning-shoes',text:'Put shoes on',icon:''}]},
    { id:'school',title:'My Day',subtitle:'First, then, next',icon:'',kind:'school',steps:[],schoolDays:[{weekday:1,label:'Monday',activity:'Preschool',pack:[{id:'liz-mon-bag',text:'Preschool bag',icon:''},{id:'liz-mon-water',text:'Water bottle',icon:''}]},{weekday:2,label:'Tuesday',activity:'OT play session',pack:[{id:'liz-tue-toy',text:'Favourite small toy',icon:''},{id:'liz-tue-water',text:'Water bottle',icon:''}]},{weekday:3,label:'Wednesday',activity:'Preschool',pack:[{id:'liz-wed-bag',text:'Preschool bag',icon:''}]},{weekday:4,label:'Thursday',activity:'Home play day',pack:[{id:'liz-thu-cards',text:'First–then cards',icon:''}]},{weekday:5,label:'Friday',activity:'Preschool',pack:[{id:'liz-fri-bag',text:'Preschool bag',icon:''}]}]},
    { id:'home',title:'Play Time',subtitle:'My first–then play routine',icon:'',kind:'tasks',steps:[{id:'liz-play-choose',text:'Choose a game',icon:''},{id:'liz-play-pack',text:'Pack it away together',icon:''}]},
    { id:'bedtime',title:'Bedtime',subtitle:'Wind down with Mum',icon:'',kind:'routine',steps:[{id:'liz-bed-pjs',text:'Put PJs on',icon:''},{id:'liz-bed-teeth',text:'Brush teeth',icon:''},{id:'liz-bed-book',text:'Choose a story',icon:''},{id:'liz-bed-cuddle',text:'Goodnight cuddle',icon:''}]},
  ],
}

function defaultFor(participantId: YouthParticipantId): YouthSupportPlan {
  return participantId === 'u-jnr' ? defaultLizzieSupportPlan : defaultYouthSupportPlan
}

function cloneDefault(participantId: YouthParticipantId): YouthSupportPlan {
  return applyCurrentDisplayCopy(JSON.parse(JSON.stringify(defaultFor(participantId))) as YouthSupportPlan, participantId)
}

function applyCurrentDisplayCopy(plan: YouthSupportPlan, participantId: YouthParticipantId): YouthSupportPlan {
  const displayPlan = {
    ...plan,
    tiles: plan.tiles.map((tile) => {
      if (tile.id === 'morning' && participantId !== 'u-jnr') return { ...tile, icon: '☀️' }
      if (tile.id === 'home') return { ...tile, subtitle: 'My after school jobs' }
      return tile
    }),
  }
  if (participantId !== 'u-jnr') return displayPlan
  return {
    ...displayPlan,
    regulationStrategies: displayPlan.regulationStrategies.map(item => ({ ...item, icon: '' })),
    tellSomeoneChoices: displayPlan.tellSomeoneChoices.map(item => ({ ...item, icon: '' })),
    rewardGoal: { ...displayPlan.rewardGoal, icon: '' },
    tiles: displayPlan.tiles.map(tile => ({
      ...tile,
      icon: '',
      steps: tile.steps.map(step => ({ ...step, icon: '' })),
      schoolDays: tile.schoolDays?.map(day => ({ ...day, pack: day.pack.map(item => ({ ...item, icon: '' })) })),
    })),
  }
}

function stringList(value: unknown, fallback: string[]) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [...fallback]
}

export function loadYouthSupportPlan(participantId: YouthParticipantId = 'u-leo'): YouthSupportPlan {
  try {
    const scopedKey = `${STORAGE_PREFIX}${participantId}`
    let raw = appStorage.getItem(scopedKey)
    if (!raw && participantId === 'u-leo') raw = appStorage.getItem(LEGACY_STORAGE_KEY)
    const parsed = JSON.parse(raw || 'null') as Partial<YouthSupportPlan> | null
    if (!parsed || !Array.isArray(parsed.regulationStrategies) || !Array.isArray(parsed.tiles)) return cloneDefault(participantId)
    const fallback = defaultFor(participantId)
    return applyCurrentDisplayCopy({
      regulationStrategies: parsed.regulationStrategies,
      tellSomeoneChoices: Array.isArray(parsed.tellSomeoneChoices) && parsed.tellSomeoneChoices.length
        ? parsed.tellSomeoneChoices
        : [...fallback.tellSomeoneChoices],
      supportProfile: {
        whatHelps: stringList(parsed.supportProfile?.whatHelps, fallback.supportProfile.whatHelps),
        whatMakesThingsWorse: stringList(parsed.supportProfile?.whatMakesThingsWorse, fallback.supportProfile.whatMakesThingsWorse),
        sensoryPreferences: stringList(parsed.supportProfile?.sensoryPreferences, fallback.supportProfile.sensoryPreferences),
        communicationPreferences: stringList(parsed.supportProfile?.communicationPreferences, fallback.supportProfile.communicationPreferences),
        signsINeedHelp: stringList(parsed.supportProfile?.signsINeedHelp, fallback.supportProfile.signsINeedHelp),
        preferredSupportPeople: stringList(parsed.supportProfile?.preferredSupportPeople, fallback.supportProfile.preferredSupportPeople),
        calmingStrategies: stringList(parsed.supportProfile?.calmingStrategies, fallback.supportProfile.calmingStrategies),
      },
      tiles: parsed.tiles,
      rewardGoal: parsed.rewardGoal && typeof parsed.rewardGoal.title === 'string'
        ? { title: parsed.rewardGoal.title, icon: parsed.rewardGoal.icon || '⭐', targetPoints: Math.max(1, Number(parsed.rewardGoal.targetPoints) || 20) }
        : { ...fallback.rewardGoal },
    }, participantId)
  } catch {
    return cloneDefault(participantId)
  }
}

export function saveYouthSupportPlan(participantId: YouthParticipantId, plan: YouthSupportPlan) {
  appStorage.setItem(`${STORAGE_PREFIX}${participantId}`, JSON.stringify(applyCurrentDisplayCopy(plan, participantId)))
  window.dispatchEvent(new CustomEvent(YOUTH_SUPPORT_PLAN_UPDATED, { detail: { participantId } }))
}

export function resetYouthSupportPlan(participantId: YouthParticipantId) {
  const plan = cloneDefault(participantId)
  saveYouthSupportPlan(participantId, plan)
  return plan
}

